<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TramiteTypesAPI from 'dashboard/api/tramiteTypes';
import ContactAPI from 'dashboard/api/contacts';

const emit = defineEmits(['created']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isCreating = ref(false);

const tramiteTypes = ref([]);

const form = ref({
  tramite_type_id: '',
  title: '',
  description: '',
  priority: 'medium',
  contact_id: '',
});

// Contact search
const contactSearchQuery = ref('');
const contactResults = ref([]);
const isSearchingContact = ref(false);
const selectedContact = ref(null);
const showContactDropdown = ref(false);

const priorityOptions = computed(() => [
  { value: 'low', label: t('TRAMITES.PRIORITY.LOW') },
  { value: 'medium', label: t('TRAMITES.PRIORITY.MEDIUM') },
  { value: 'high', label: t('TRAMITES.PRIORITY.HIGH') },
  { value: 'urgent', label: t('TRAMITES.PRIORITY.URGENT') },
]);

const isValid = computed(
  () => Boolean(form.value.tramite_type_id) && Boolean(form.value.title.trim())
);

const resetForm = () => {
  form.value = {
    tramite_type_id: '',
    title: '',
    description: '',
    priority: 'medium',
    contact_id: '',
  };
  selectedContact.value = null;
  contactSearchQuery.value = '';
  contactResults.value = [];
  showContactDropdown.value = false;
};

const fetchTramiteTypes = async () => {
  try {
    const { data } = await TramiteTypesAPI.get();
    tramiteTypes.value = data || [];
  } catch {
    tramiteTypes.value = [];
  }
};

const searchContacts = async () => {
  const q = contactSearchQuery.value.trim();
  if (!q) {
    contactResults.value = [];
    return;
  }
  isSearchingContact.value = true;
  try {
    const { data } = await ContactAPI.search(q);
    contactResults.value = data?.payload || [];
    showContactDropdown.value = true;
  } catch {
    contactResults.value = [];
  } finally {
    isSearchingContact.value = false;
  }
};

const selectContact = contact => {
  selectedContact.value = contact;
  form.value.contact_id = contact.id;
  contactSearchQuery.value = contact.name;
  showContactDropdown.value = false;
};

const clearContact = () => {
  selectedContact.value = null;
  form.value.contact_id = '';
  contactSearchQuery.value = '';
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};
const close = () => dialogRef.value?.close();

const submit = async () => {
  if (!isValid.value) {
    useAlert(t('TRAMITES.CREATE.VALIDATION'));
    return;
  }
  isCreating.value = true;
  try {
    const payload = {
      tramite_type_id: form.value.tramite_type_id,
      title: form.value.title.trim(),
      description: form.value.description.trim(),
      priority: form.value.priority,
    };
    if (form.value.contact_id) payload.contact_id = form.value.contact_id;
    const tramite = await store.dispatch('tramites/createTramite', payload);
    useAlert(t('TRAMITES.CREATE.SUCCESS'));
    close();
    emit('created', tramite);
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TRAMITES.CREATE.ERROR'));
  } finally {
    isCreating.value = false;
  }
};

onMounted(fetchTramiteTypes);

defineExpose({ open, close, dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('TRAMITES.CREATE.TITLE')"
    :confirm-button-label="t('TRAMITES.CREATE.SUBMIT')"
    :cancel-button-label="t('TRAMITES.CREATE.CANCEL')"
    :is-loading="isCreating"
    :disable-confirm-button="!isValid"
    @confirm="submit"
  >
    <div class="flex flex-col gap-4">
      <!-- Type -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TRAMITES.CREATE.TYPE_LABEL') }}
        </label>
        <select
          v-model="form.tramite_type_id"
          class="w-full h-9 px-3 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand"
        >
          <option value="" disabled>
            {{ t('TRAMITES.CREATE.TYPE_PLACEHOLDER') }}
          </option>
          <option v-for="type in tramiteTypes" :key="type.id" :value="type.id">
            {{ type.name }}
          </option>
        </select>
      </div>

      <!-- Title -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TRAMITES.CREATE.TITLE_LABEL') }}
        </label>
        <input
          v-model="form.title"
          type="text"
          :placeholder="t('TRAMITES.CREATE.TITLE_PLACEHOLDER')"
          class="w-full h-9 px-3 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
        />
      </div>

      <!-- Description -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TRAMITES.CREATE.DESCRIPTION_LABEL') }}
        </label>
        <textarea
          v-model="form.description"
          rows="3"
          :placeholder="t('TRAMITES.CREATE.DESCRIPTION_PLACEHOLDER')"
          class="w-full px-3 py-2 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
        />
      </div>

      <!-- Priority -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TRAMITES.CREATE.PRIORITY_LABEL') }}
        </label>
        <select
          v-model="form.priority"
          class="w-full h-9 px-3 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand"
        >
          <option
            v-for="opt in priorityOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>

      <!-- Contact (optional) -->
      <div class="relative flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TRAMITES.CREATE.CONTACT_LABEL') }}
        </label>
        <div class="flex items-center gap-2">
          <input
            v-model="contactSearchQuery"
            type="text"
            :placeholder="t('TRAMITES.CREATE.CONTACT_PLACEHOLDER')"
            class="w-full h-9 px-3 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
            @input="searchContacts"
          />
          <button
            v-if="selectedContact"
            type="button"
            class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            @click="clearContact"
          >
            <span class="i-lucide-x" />
          </button>
        </div>
        <ul
          v-if="showContactDropdown && contactResults.length"
          class="absolute left-0 right-0 z-50 py-1 mt-1 overflow-y-auto border rounded-lg shadow-lg top-full bg-n-solid-2 border-n-weak max-h-44"
        >
          <li
            v-for="contact in contactResults"
            :key="contact.id"
            class="px-3 py-1.5 text-sm cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
            @click="selectContact(contact)"
          >
            {{ contact.name }}
          </li>
        </ul>
      </div>
    </div>
  </Dialog>
</template>
